"use client";

import React, { useEffect, useState } from "react";
import { Menu, ChevronDown, Phone } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu, MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

// Recursive dropdown items renderer for desktop submenus
function RecursiveDropdownList({
  items,
  depth = 0
}: {
  items: MenuItem[];
  depth?: number;
}) {
  return <ul className={depth === 0 ? "min-w-[200px] p-2 space-y-1" : "pl-3 mt-1 space-y-1 border-l border-border/50"}>
      {items.map((child, i) => <li key={child.id || `child-${depth}-${i}`} data-index={i}>
          {child.children && child.children.length > 0 ? <div className="space-y-1">
              <Link to={child.href || "#"} className="block px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors rounded-sm">
                {child.label}
              </Link>
              <RecursiveDropdownList items={child.children} depth={depth + 1} />
            </div> : <NavigationMenuLink asChild>
              <Link to={child.href || "#"} className="block px-3 py-2 text-sm text-foreground hover:bg-muted/60 hover:text-primary rounded-md transition-colors">
                {child.label}
              </Link>
            </NavigationMenuLink>}
        </li>)}
    </ul>;
}

// Desktop navigation consumer
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex items-center gap-2">
        <div className="h-8 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-24 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="flex items-center gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id || `menu-item-${i}`} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="bg-transparent hover:bg-transparent text-foreground hover:text-primary focus:bg-transparent data-[state=open]:text-primary transition-colors text-sm font-medium px-3 py-2">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="bg-card border border-border shadow-md rounded-lg p-1 min-w-[210px]">
                  <RecursiveDropdownList items={item.children} />
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="group relative inline-flex items-center px-3 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors">
                  <span>{item.label}</span>
                  <span className="absolute bottom-1 left-3 right-3 h-[2px] bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left" />
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}

// Recursive list renderer for mobile drawer menu
function MobileMenuRecursiveList({
  items,
  depth = 0,
  onItemClick
}: {
  items: MenuItem[];
  depth?: number;
  onItemClick: () => void;
}) {
  return <ul className={depth === 0 ? "flex flex-col gap-1 w-full" : "pl-4 mt-1 flex flex-col gap-1 border-l border-border/60"}>
      {items.map((item, i) => <li key={item.id || `mobile-${depth}-${i}`} data-index={i} className="flex flex-col">
          {item.children && item.children.length > 0 ? <div className="flex flex-col py-1">
              <Link to={item.href || "#"} onClick={onItemClick} className="py-2 text-sm font-semibold text-foreground flex items-center justify-between hover:text-primary transition-colors">
                <span>{item.label}</span>
                <ChevronDown className="size-4 text-muted-foreground" />
              </Link>
              <MobileMenuRecursiveList items={item.children} depth={depth + 1} onItemClick={onItemClick} />
            </div> : <Link to={item.href || "#"} onClick={onItemClick} className="py-2.5 text-sm text-foreground hover:text-primary transition-colors font-medium">
              {item.label}
            </Link>}
        </li>)}
    </ul>;
}

// Mobile navigation consumer
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3 py-4">
        <div className="h-7 w-3/4 bg-muted animate-pulse rounded-md" />
        <div className="h-7 w-1/2 bg-muted animate-pulse rounded-md" />
        <div className="h-7 w-2/3 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <MobileMenuRecursiveList items={menuItems} onItemClick={onItemClick} />;
}

// Exact Brand Logo Component with -h suffixed IDs
function BrandLogo() {
  return <div className="inline-flex items-center gap-2.5">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120" role="img" aria-label="Brookhaven Elevation Renovations logo" className="h-[46px] w-auto shrink-0">
        <defs>
          <linearGradient id="gL-h" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#9fd3ff" stopOpacity=".85" />
            <stop offset="1" stopColor="#1e3a5f" stopOpacity=".75" />
          </linearGradient>
          <linearGradient id="gR-h" x1="1" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#e9f5ff" stopOpacity=".9" />
            <stop offset="1" stopColor="#5b8db8" stopOpacity=".6" />
          </linearGradient>
          <linearGradient id="gRoofL-h" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#ffffff" stopOpacity=".95" />
            <stop offset="1" stopColor="#b7dcf5" stopOpacity=".55" />
          </linearGradient>
          <linearGradient id="gRoofR-h" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#d9eefc" stopOpacity=".8" />
            <stop offset="1" stopColor="#3b6a94" stopOpacity=".55" />
          </linearGradient>
          <linearGradient id="cu-h" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#f3c08a" />
            <stop offset=".5" stopColor="#c8793a" />
            <stop offset="1" stopColor="#8a4a1f" />
          </linearGradient>
          <linearGradient id="base-h" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#c8793a" stopOpacity=".35" />
            <stop offset="1" stopColor="#c8793a" stopOpacity="0" />
          </linearGradient>
          <filter id="sh-h" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="4" stdDeviation="3" floodColor="#1e3a5f" floodOpacity=".25" />
          </filter>
        </defs>
        <polygon points="60,104 18,80 60,56 102,80" fill="url(#base-h)" />
        <g filter="url(#sh-h)">
          <polygon points="22,58 60,80 60,104 22,82" fill="url(#gL-h)" stroke="#1e3a5f" strokeWidth="1.6" strokeLinejoin="round" />
          <polygon points="60,80 98,58 98,82 60,104" fill="url(#gR-h)" stroke="#1e3a5f" strokeWidth="1.6" strokeLinejoin="round" />
          <polygon points="34,74 44,80 44,96 34,90" fill="#1e3a5f" opacity=".55" />
          <polygon points="70,82 86,73 86,84 70,93" fill="#ffffff" opacity=".7" stroke="#c8793a" strokeWidth="1" />
          <polygon points="22,58 41,45 79,23 60,36" fill="url(#gRoofR-h)" stroke="#1e3a5f" strokeWidth="1.6" strokeLinejoin="round" />
          <polygon points="22,58 60,80 41,45" fill="url(#gL-h)" stroke="#1e3a5f" strokeWidth="1.6" strokeLinejoin="round" />
          <polygon points="60,80 98,58 79,23 41,45" fill="url(#gRoofL-h)" stroke="#1e3a5f" strokeWidth="1.6" strokeLinejoin="round" />
          <line x1="41" y1="45" x2="79" y2="23" stroke="url(#cu-h)" strokeWidth="3.2" strokeLinecap="round" />
          <circle cx="41" cy="62" r="4" fill="#fff" opacity=".75" stroke="#c8793a" strokeWidth="1" />
          <path d="M66 72 L84 40" stroke="#fff" strokeWidth="2" strokeLinecap="round" opacity=".8" />
          <path d="M27 64 L27 78" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" opacity=".6" />
        </g>
        <circle cx="79" cy="23" r="2.6" fill="#f3c08a" />
      </svg>
      <div className="flex flex-col text-left">
        <span className="font-serif text-sm sm:text-base font-bold tracking-tight text-foreground leading-tight">
          Brookhaven Elevation
        </span>
        <span className="text-[10px] tracking-[0.22em] font-semibold uppercase text-[#c8793a] leading-none mt-0.5">
          RENOVATIONS
        </span>
      </div>
    </div>;
}
export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const shouldReduceMotion = useReducedMotion();
  const handleEstimateClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const formElement = document.getElementById("estimaterequestform");
    if (formElement) {
      e.preventDefault();
      formElement.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  };

  // Smooth scroll support for same-page hash links
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <>
      {/* Frosted-glass isometric scene across viewport */}
      <div className="fixed inset-0 -z-10 pointer-events-none select-none overflow-hidden bg-[#f7f4ee] opacity-25" aria-hidden="true">
        {/* Soft blurred navy & copper light orbs */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div className="absolute -top-24 left-[15%] w-96 h-96 rounded-full bg-[#1e3a5f]/15 blur-3xl" initial={{
          x: 0,
          y: 0,
          scale: 1
        }} animate={shouldReduceMotion ? {
          x: 0,
          y: 0,
          scale: 1
        } : {
          x: [-30, 30, -30],
          y: [-15, 20, -15],
          scale: [1, 1.08, 1]
        }} transition={shouldReduceMotion ? {
          duration: 0
        } : {
          duration: 24,
          repeat: Infinity,
          ease: "easeInOut"
        }} />
          <motion.div className="absolute top-1/3 -right-20 w-[420px] h-[420px] rounded-full bg-[#c8793a]/18 blur-3xl" initial={{
          x: 0,
          y: 0,
          scale: 1
        }} animate={shouldReduceMotion ? {
          x: 0,
          y: 0,
          scale: 1
        } : {
          x: [25, -25, 25],
          y: [15, -20, 15],
          scale: [1, 1.06, 1]
        }} transition={shouldReduceMotion ? {
          duration: 0
        } : {
          duration: 28,
          repeat: Infinity,
          ease: "easeInOut"
        }} />
          <motion.div className="absolute bottom-10 left-1/3 w-80 h-80 rounded-full bg-[#9fd3ff]/20 blur-3xl" initial={{
          x: 0,
          y: 0
        }} animate={shouldReduceMotion ? {
          x: 0,
          y: 0
        } : {
          x: [-20, 20, -20],
          y: [-10, 15, -10]
        }} transition={shouldReduceMotion ? {
          duration: 0
        } : {
          duration: 20,
          repeat: Infinity,
          ease: "easeInOut"
        }} />
          <motion.div className="hidden sm:block absolute top-2/3 right-1/4 w-72 h-72 rounded-full bg-[#f3c08a]/20 blur-2xl" initial={{
          x: 0,
          y: 0
        }} animate={shouldReduceMotion ? {
          x: 0,
          y: 0
        } : {
          x: [15, -15, 15],
          y: [-20, 10, -20]
        }} transition={shouldReduceMotion ? {
          duration: 0
        } : {
          duration: 22,
          repeat: Infinity,
          ease: "easeInOut"
        }} />
        </div>

        {/* Diagonal drifting isometric architectural scene */}
        <motion.div className="w-full h-full min-w-[1000px]" initial={{
        x: 0,
        y: 0
      }} animate={shouldReduceMotion ? {
        x: 0,
        y: 0
      } : {
        x: [-28, 28, -28],
        y: [-14, 14, -14]
      }} transition={shouldReduceMotion ? {
        duration: 0
      } : {
        duration: 32,
        repeat: Infinity,
        ease: "easeInOut"
      }}>
          <svg className="w-full h-full object-cover min-w-[1200px]" viewBox="0 0 1440 900" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              {/* Glass tile gradients */}
              <linearGradient id="bgGlassTile1" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="0.8" />
                <stop offset="50%" stopColor="#eaf4fc" stopOpacity="0.45" />
                <stop offset="100%" stopColor="#b7dcf5" stopOpacity="0.25" />
              </linearGradient>
              <linearGradient id="bgGlassTile2" x1="0" y1="1" x2="1" y2="0">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="0.85" />
                <stop offset="60%" stopColor="#d9eefc" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#ffffff" stopOpacity="0.1" />
              </linearGradient>
              <linearGradient id="bgCopperEdge" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#f3c08a" />
                <stop offset="50%" stopColor="#c8793a" />
                <stop offset="100%" stopColor="#8a4a1f" />
              </linearGradient>
              <linearGradient id="bgStairStep" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#b7dcf5" stopOpacity="0.35" />
              </linearGradient>
            </defs>

            {/* Drifting Translucent Glass Floor Tiles */}
            <g stroke="#1e3a5f" strokeOpacity="0.25" strokeWidth="1">
              {/* Tile 1 */}
              <polygon points="220,540 340,470 460,540 340,610" fill="url(#bgGlassTile1)" />
              <line x1="220" y1="540" x2="340" y2="470" stroke="#ffffff" strokeWidth="1.5" strokeOpacity="0.9" />

              {/* Tile 2 */}
              <polygon points="340,610 460,540 580,610 460,680" fill="url(#bgGlassTile2)" />
              <line x1="340" y1="610" x2="460" y2="540" stroke="#ffffff" strokeWidth="1.5" strokeOpacity="0.9" />

              {/* Tile 3 */}
              <polygon points="460,540 580,470 700,540 580,610" fill="url(#bgGlassTile1)" />
              <line x1="460" y1="540" x2="580" y2="470" stroke="#ffffff" strokeWidth="1.5" strokeOpacity="0.9" />

              {/* Tile 4 (Desktop/tablet view) */}
              <polygon className="hidden sm:block" points="580,610 700,540 820,610 700,680" fill="url(#bgGlassTile2)" />
              <line className="hidden sm:block" x1="580" y1="610" x2="700" y2="540" stroke="#ffffff" strokeWidth="1.5" strokeOpacity="0.9" />

              {/* Tile 5 (Far Right) */}
              <polygon className="hidden sm:block" points="700,540 820,470 940,540 820,610" fill="url(#bgGlassTile1)" />
              <line className="hidden sm:block" x1="700" y1="540" x2="820" y2="470" stroke="#ffffff" strokeWidth="1.5" strokeOpacity="0.9" />

              {/* Distant grid lines */}
              <line x1="100" y1="610" x2="460" y2="400" stroke="#1e3a5f" strokeOpacity="0.12" strokeDasharray="6 6" />
              <line x1="340" y1="750" x2="700" y2="540" stroke="#1e3a5f" strokeOpacity="0.12" strokeDasharray="6 6" />
              <line x1="580" y1="750" x2="940" y2="540" stroke="#1e3a5f" strokeOpacity="0.12" strokeDasharray="6 6" />
            </g>

            {/* Left Zone: Faint Glass Modern Kitchen Island with Copper Countertop */}
            <g className="opacity-90">
              {/* Island Base (Translucent glass panels) */}
              <polygon points="120,420 120,490 230,550 230,480" fill="#b7dcf5" fillOpacity="0.25" stroke="#1e3a5f" strokeWidth="1" strokeOpacity="0.35" />
              <polygon points="230,480 230,550 350,480 350,410" fill="#ffffff" fillOpacity="0.35" stroke="#1e3a5f" strokeWidth="1" strokeOpacity="0.35" />
              {/* Internal shelf/divider detail */}
              <line x1="120" y1="455" x2="230" y2="515" stroke="#ffffff" strokeWidth="1.2" strokeOpacity="0.75" />
              <line x1="230" y1="515" x2="350" y2="445" stroke="#ffffff" strokeWidth="1.2" strokeOpacity="0.6" />

              {/* Countertop Top Facet */}
              <polygon points="120,420 240,350 350,410 230,480" fill="url(#bgGlassTile1)" stroke="#1e3a5f" strokeWidth="1" strokeOpacity="0.3" />
              {/* Specular highlight along top front back edge */}
              <line x1="120" y1="420" x2="240" y2="350" stroke="#ffffff" strokeWidth="1.8" strokeOpacity="0.85" />

              {/* Copper Countertop Leading Edge */}
              <line x1="120" y1="420" x2="230" y2="480" stroke="url(#bgCopperEdge)" strokeWidth="3" strokeLinecap="round" />
              <line x1="230" y1="480" x2="350" y2="410" stroke="url(#bgCopperEdge)" strokeWidth="3" strokeLinecap="round" />
              {/* Countertop edge bevel shine */}
              <line x1="122" y1="419" x2="228" y2="477" stroke="#ffffff" strokeWidth="1" strokeOpacity="0.9" />
            </g>

            {/* Right Zone: Glass Isometric Staircase Rising with Steps */}
            <g className="hidden sm:block">
              {/* Step 1 */}
              <motion.g initial={{
              opacity: 0.3
            }} animate={shouldReduceMotion ? {
              opacity: 0.7
            } : {
              opacity: [0.35, 0.9, 0.35]
            }} transition={shouldReduceMotion ? {
              duration: 0
            } : {
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 0
            }}>
                <polygon points="1060,560 1120,525 1180,560 1120,595" fill="url(#bgStairStep)" stroke="#1e3a5f" strokeWidth="1" strokeOpacity="0.3" />
                <polygon points="1060,560 1060,575 1120,610 1120,595" fill="#9fd3ff" fillOpacity="0.35" stroke="#1e3a5f" strokeWidth="1" strokeOpacity="0.3" />
                <polygon points="1120,595 1120,610 1180,575 1180,560" fill="#ffffff" fillOpacity="0.5" stroke="#1e3a5f" strokeWidth="1" strokeOpacity="0.3" />
                <line x1="1060" y1="560" x2="1120" y2="525" stroke="#ffffff" strokeWidth="1.5" strokeOpacity="0.85" />
              </motion.g>

              {/* Step 2 */}
              <motion.g initial={{
              opacity: 0.3
            }} animate={shouldReduceMotion ? {
              opacity: 0.7
            } : {
              opacity: [0.35, 0.9, 0.35]
            }} transition={shouldReduceMotion ? {
              duration: 0
            } : {
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1
            }}>
                <polygon points="1100,510 1160,475 1220,510 1160,545" fill="url(#bgStairStep)" stroke="#1e3a5f" strokeWidth="1" strokeOpacity="0.3" />
                <polygon points="1100,510 1100,525 1160,560 1160,545" fill="#9fd3ff" fillOpacity="0.35" stroke="#1e3a5f" strokeWidth="1" strokeOpacity="0.3" />
                <polygon points="1160,545 1160,560 1220,525 1220,510" fill="#ffffff" fillOpacity="0.5" stroke="#1e3a5f" strokeWidth="1" strokeOpacity="0.3" />
                <line x1="1100" y1="510" x2="1160" y2="475" stroke="#ffffff" strokeWidth="1.5" strokeOpacity="0.85" />
              </motion.g>

              {/* Step 3 */}
              <motion.g initial={{
              opacity: 0.3
            }} animate={shouldReduceMotion ? {
              opacity: 0.7
            } : {
              opacity: [0.35, 0.9, 0.35]
            }} transition={shouldReduceMotion ? {
              duration: 0
            } : {
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2
            }}>
                <polygon points="1140,460 1200,425 1260,460 1200,495" fill="url(#bgStairStep)" stroke="#1e3a5f" strokeWidth="1" strokeOpacity="0.3" />
                <polygon points="1140,460 1140,475 1200,510 1200,495" fill="#9fd3ff" fillOpacity="0.35" stroke="#1e3a5f" strokeWidth="1" strokeOpacity="0.3" />
                <polygon points="1200,495 1200,510 1260,475 1260,460" fill="#ffffff" fillOpacity="0.5" stroke="#1e3a5f" strokeWidth="1" strokeOpacity="0.3" />
                <line x1="1140" y1="460" x2="1200" y2="425" stroke="#ffffff" strokeWidth="1.5" strokeOpacity="0.85" />
              </motion.g>

              {/* Step 4 */}
              <motion.g initial={{
              opacity: 0.3
            }} animate={shouldReduceMotion ? {
              opacity: 0.7
            } : {
              opacity: [0.35, 0.9, 0.35]
            }} transition={shouldReduceMotion ? {
              duration: 0
            } : {
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 3
            }}>
                <polygon points="1180,410 1240,375 1300,410 1240,445" fill="url(#bgStairStep)" stroke="#1e3a5f" strokeWidth="1" strokeOpacity="0.3" />
                <polygon points="1180,410 1180,425 1240,460 1240,445" fill="#9fd3ff" fillOpacity="0.35" stroke="#1e3a5f" strokeWidth="1" strokeOpacity="0.3" />
                <polygon points="1240,445 1240,460 1300,425 1300,410" fill="#ffffff" fillOpacity="0.5" stroke="#1e3a5f" strokeWidth="1" strokeOpacity="0.3" />
                <line x1="1180" y1="410" x2="1240" y2="375" stroke="#ffffff" strokeWidth="1.5" strokeOpacity="0.85" />
              </motion.g>

              {/* Glass Guardrail & Copper Top Cap */}
              <polygon points="1060,540 1240,315 1240,360 1060,585" fill="#ffffff" fillOpacity="0.18" stroke="#1e3a5f" strokeWidth="0.8" strokeOpacity="0.25" />
              <line x1="1060" y1="540" x2="1240" y2="315" stroke="url(#bgCopperEdge)" strokeWidth="2.5" strokeLinecap="round" />
              <line x1="1061" y1="539" x2="1239" y2="314" stroke="#ffffff" strokeWidth="0.8" strokeOpacity="0.8" />
            </g>
          </svg>
        </motion.div>
      </div>

      <section id="header" data-nav="light" className="sticky top-0 z-50 w-full">
        <motion.header initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: "easeOut"
      }} className="relative w-full overflow-hidden backdrop-blur-md bg-background/85 border-b border-border/60 transition-[background-color,border-color]">
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-4">
            {/* Logo element with exact inline SVG and brand wordmark */}
            <div className="flex shrink-0 items-center">
              <Link to="/" className="inline-flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md">
                <BrandLogo />
              </Link>
            </div>

            {/* Desktop Center Navigation */}
            <nav className="hidden lg:flex items-center justify-center flex-1">
              <MenuProvider menu_id="46">
                <DesktopMenuConsumer />
              </MenuProvider>
            </nav>

            {/* Right Action CTA & Mobile Trigger */}
            <div className="flex items-center gap-3">
              <div className="hidden sm:inline-flex">
                <Button asChild className="bg-primary text-primary-foreground font-semibold px-6 py-2.5 rounded-lg shadow-sm hover:bg-primary/90 hover:-translate-y-0.5 active:translate-y-0 transition-[transform,background-color,box-shadow]">
                  <Link to="/ContactRequestAnEstimatePage" onClick={handleEstimateClick}>
                    Request an Estimate
                  </Link>
                </Button>
              </div>

              {/* Mobile / Tablet Drawer Menu */}
              <div className="lg:hidden">
                <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="icon" className="text-foreground hover:bg-muted/70 focus-visible:ring-2 focus-visible:ring-ring" aria-label="Open navigation menu">
                      <Menu className="size-6" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-[85vw] max-w-sm flex flex-col justify-between p-6">
                    <div className="flex flex-col gap-6 overflow-y-auto">
                      <SheetHeader className="text-left p-0 pb-4 border-b border-border">
                        <SheetTitle className="sr-only">Site Navigation</SheetTitle>
                        <SheetDescription className="sr-only">
                          Navigate through pages and request a remodeling estimate.
                        </SheetDescription>
                        <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                          <BrandLogo />
                        </Link>
                      </SheetHeader>

                      <nav className="flex flex-col w-full">
                        <MenuProvider menu_id="46">
                          <MobileMenuConsumer onItemClick={() => setMobileMenuOpen(false)} />
                        </MenuProvider>
                      </nav>
                    </div>

                    <div className="pt-6 border-t border-border mt-auto">
                      <Button asChild className="w-full bg-primary text-primary-foreground font-semibold py-3 rounded-lg shadow-sm hover:bg-primary/90 transition-colors">
                        <Link to="/ContactRequestAnEstimatePage" onClick={e => {
                        setMobileMenuOpen(false);
                        handleEstimateClick(e);
                      }}>
                          Request an Estimate
                        </Link>
                      </Button>
                      <p className="mt-4 text-xs text-center text-muted-foreground">
                        <a href="tel:+14708479076" className="hover:text-primary transition-colors inline-flex items-center justify-center gap-1.5 focus-visible:underline focus-visible:outline-none">
                          (470) 847-9076
                        </a>
                      </p>
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </div>
          </div>
        </motion.header>
      </section>

      {/* Sticky mobile-only bottom bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-t border-border px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] flex items-center gap-3 shadow-lg">
        <Button asChild variant="outline" className="flex-1 font-semibold border-border hover:border-primary/50 text-foreground py-2.5 h-11">
          <a href="tel:+14708479076" className="inline-flex items-center justify-center gap-2">
            <Phone className="size-4 text-primary" aria-hidden="true" />
            <span>Call</span>
          </a>
        </Button>

        <Button asChild className="flex-1 bg-primary text-primary-foreground font-semibold py-2.5 h-11 shadow-sm hover:bg-primary/90">
          <Link to="/ContactRequestAnEstimatePage" onClick={handleEstimateClick}>
            Request an Estimate
          </Link>
        </Button>
      </div>
    </>;
}